/**
 * @file routes/_app/settings/roles.$code.tsx
 * @description 角色詳情頁（Task 11），三 Tab：基本信息 / 功能權限 / 菜單可見性。
 *
 * Tab URL 同步：`?tab=info|capabilities|menu`，跟隨 TanStack Router searchParams。
 *
 * Tab 說明：
 * 1. 基本信息：Form（名稱/描述/工作台類型/狀態 Switch） → PATCH。
 *    admin 角色整頁只讀 + Alert「系統管理員角色鎖定不可編輯」。
 * 2. 功能權限：按 CAPABILITY_GROUPS 分組的 Checkbox 面板，底部「儲存」→ PUT capabilities。
 * 3. 菜單可見性：該角色 workbenchType 對應 MENU_TREES 渲染 antd Tree（checkable）；
 *    能力不足節點 disabled + title 後綴；未勾選葉 key 收集 → PUT menu-hidden。
 */

import { createFileRoute, useNavigate } from '@tanstack/react-router';
import {
  Alert,
  Button,
  Checkbox,
  Divider,
  Form,
  Select,
  Spin,
  Switch,
  Tabs,
  Tree,
  Typography,
  Input,
} from 'antd';
import { ArrowLeft, Save } from 'lucide-react';
import { useState, useCallback } from 'react';
import type { DataNode, TreeProps } from 'antd/es/tree';
import { z } from 'zod';
import {
  CAPABILITY_GROUPS,
  CAPABILITY_LABELS,
  WORKBENCH_TYPES,
  WORKBENCH_TYPE_LABELS,
} from '@zhcb/shared';
import {
  useRoleDetail,
  useUpdateRole,
  usePutCapabilities,
  usePutMenuHidden,
} from '@/services/roles';
import { PageShell } from '@/components/shell';
import { MENU_TREES, type MenuItemDef } from '@/components/layout/menu-registry';
import { color, weight } from '@/styles/tokens';

// ─── 路由定義 ────────────────────────────────────────────────────────────────

/** URL searchParams schema（tab 欄位可選） */
const searchSchema = z.object({
  tab: z.enum(['info', 'capabilities', 'menu']).optional().catch('info'),
});

export const Route = createFileRoute('/_app/settings/roles/$code')({
  component: RoleDetailPage,
  validateSearch: (s) => searchSchema.parse(s),
});

// ─── 工具函數 ────────────────────────────────────────────────────────────────

/**
 * 將 MenuItemDef 樹遞歸轉換為 antd Tree DataNode 樹。
 *
 * @param items       MenuItemDef[]
 * @param capSet      當前角色已擁有的能力集 Set<string>
 * @returns           DataNode[]（antd Tree 所需格式）
 */
function buildTreeNodes(items: MenuItemDef[], capSet: Set<string>): DataNode[] {
  return items.map((def) => {
    /** 判斷該菜單項的前置能力是否已勾選 */
    const capSatisfied = !def.requiredCapability || capSet.has(def.requiredCapability);

    /** 缺乏能力時在 title 後附上提示 */
    const titleText = def.label;
    const titleNode = capSatisfied ? (
      titleText
    ) : (
      <span style={{ color: color.text.muted }}>
        {titleText}
        <span style={{ fontSize: 11, marginLeft: 4 }}>
          （需先勾選：
          {def.requiredCapability
            ? (CAPABILITY_LABELS[def.requiredCapability] ?? def.requiredCapability)
            : ''}
          ）
        </span>
      </span>
    );

    if (def.children?.length) {
      return {
        key: def.key,
        title: titleNode,
        checkable: true,
        disabled: !capSatisfied,
        children: buildTreeNodes(def.children, capSet),
      } satisfies DataNode;
    }

    return {
      key: def.key,
      title: titleNode,
      checkable: true,
      isLeaf: true,
      disabled: !capSatisfied,
    } satisfies DataNode;
  });
}

/**
 * 遞歸收集所有葉節點 key（以 `/` 開頭的路由 key）。
 *
 * @param items MenuItemDef[]
 * @returns     所有葉 key 陣列
 */
function collectAllLeafKeys(items: MenuItemDef[]): string[] {
  const keys: string[] = [];
  for (const def of items) {
    if (def.children?.length) {
      keys.push(...collectAllLeafKeys(def.children));
    } else if (def.key.startsWith('/')) {
      keys.push(def.key);
    }
  }
  return keys;
}

// ─── 基本信息 Tab ────────────────────────────────────────────────────────────

interface BasicInfoTabProps {
  code: string;
  isAdmin: boolean;
}

/** 基本信息 Tab（名稱/描述/工作台類型/狀態 Switch → PATCH） */
function BasicInfoTab({ code, isAdmin }: BasicInfoTabProps) {
  const { data, isLoading } = useRoleDetail(code);
  const updateRole = useUpdateRole(code);
  const [form] = Form.useForm();

  const item = data?.item;

  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}>
        <Spin />
      </div>
    );
  }

  if (!item) return null;

  /** admin 角色整頁只讀 */
  if (isAdmin) {
    return (
      <Alert
        type="warning"
        showIcon
        message="系統管理員角色鎖定不可編輯"
        description="admin 角色的名稱、能力與菜單不允許修改，以確保系統至少有一個超級管理員。"
        style={{ maxWidth: 640 }}
      />
    );
  }

  const handleSave = () => {
    void form
      .validateFields()
      .then(
        (values: {
          name: string;
          description?: string;
          workbenchType: string;
          status: boolean;
        }) => {
          updateRole.mutate({
            name: values.name,
            description: values.description || undefined,
            workbenchType: values.workbenchType as (typeof WORKBENCH_TYPES)[number],
            status: values.status ? 'active' : 'disabled',
          });
        },
      );
  };

  return (
    <Form
      form={form}
      layout="vertical"
      initialValues={{
        name: item.name,
        description: item.description ?? '',
        workbenchType: item.workbenchType,
        status: item.status === 'active',
      }}
      style={{ maxWidth: 560 }}
    >
      <Form.Item
        label="角色名稱"
        name="name"
        rules={[{ required: true, message: '請輸入角色名稱' }]}
      >
        <Input maxLength={100} />
      </Form.Item>

      <Form.Item label="描述" name="description">
        <Input.TextArea rows={3} maxLength={500} showCount />
      </Form.Item>

      <Form.Item
        label="工作台類型"
        name="workbenchType"
        rules={[{ required: true, message: '請選擇工作台類型' }]}
      >
        <Select
          options={WORKBENCH_TYPES.map((t) => ({
            value: t,
            label: WORKBENCH_TYPE_LABELS[t],
          }))}
        />
      </Form.Item>

      <Form.Item label="狀態" name="status" valuePropName="checked">
        <Switch checkedChildren="啟用" unCheckedChildren="停用" />
      </Form.Item>

      <Form.Item>
        <Button
          type="primary"
          icon={<Save size={14} />}
          loading={updateRole.isPending}
          onClick={handleSave}
        >
          儲存
        </Button>
      </Form.Item>
    </Form>
  );
}

// ─── 功能權限 Tab ────────────────────────────────────────────────────────────

interface CapabilitiesTabProps {
  code: string;
  isAdmin: boolean;
}

/** 功能權限 Tab（按 CAPABILITY_GROUPS 分組的 Checkbox 面板 → PUT capabilities） */
function CapabilitiesTab({ code, isAdmin }: CapabilitiesTabProps) {
  const { data, isLoading } = useRoleDetail(code);
  const putCapabilities = usePutCapabilities(code);
  const [selected, setSelected] = useState<Set<string> | null>(null);

  const item = data?.item;

  /** 初始化 selected（僅首次，避免用戶選中被覆蓋） */
  const currentCapSet = selected ?? (item ? new Set<string>(item.capabilities) : new Set<string>());

  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}>
        <Spin />
      </div>
    );
  }

  if (!item) return null;

  if (isAdmin) {
    return (
      <Alert
        type="warning"
        showIcon
        message="系統管理員角色鎖定不可編輯"
        style={{ maxWidth: 640 }}
      />
    );
  }

  /** 切換單一能力 */
  const toggleCap = (cap: string, checked: boolean) => {
    const next = new Set(currentCapSet);
    if (checked) {
      next.add(cap);
    } else {
      next.delete(cap);
    }
    setSelected(next);
  };

  /** 切換分組全選 */
  const toggleGroup = (caps: readonly string[], checked: boolean) => {
    const next = new Set(currentCapSet);
    for (const cap of caps) {
      if (checked) {
        next.add(cap);
      } else {
        next.delete(cap);
      }
    }
    setSelected(next);
  };

  /** 儲存：把 Set 轉為陣列調 PUT */
  const handleSave = () => {
    putCapabilities.mutate(Array.from(currentCapSet));
  };

  return (
    <div style={{ maxWidth: 800 }}>
      {CAPABILITY_GROUPS.map((group) => {
        const groupCaps = group.capabilities;
        const checkedCount = groupCaps.filter((c) => currentCapSet.has(c)).length;
        const allChecked = checkedCount === groupCaps.length;
        const someChecked = checkedCount > 0 && checkedCount < groupCaps.length;

        return (
          <div key={group.key} style={{ marginBottom: 24 }}>
            {/* 分組標題 + 全選 Checkbox */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                marginBottom: 10,
                paddingBottom: 6,
                borderBottom: `1px solid ${color.stone[200]}`,
              }}
            >
              <Checkbox
                indeterminate={someChecked}
                checked={allChecked}
                onChange={(e) => toggleGroup(groupCaps, e.target.checked)}
              />
              <Typography.Text style={{ fontWeight: weight.semibold }}>
                {group.title}
              </Typography.Text>
            </div>

            {/* 分組內能力 Checkbox 列表 */}
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
                gap: '6px 12px',
                paddingLeft: 24,
              }}
            >
              {groupCaps.map((cap) => (
                <Checkbox
                  key={cap}
                  checked={currentCapSet.has(cap)}
                  onChange={(e) => toggleCap(cap, e.target.checked)}
                >
                  <span style={{ fontSize: 13 }}>{CAPABILITY_LABELS[cap] ?? cap}</span>
                </Checkbox>
              ))}
            </div>
          </div>
        );
      })}

      <Divider />

      <Button
        type="primary"
        icon={<Save size={14} />}
        loading={putCapabilities.isPending}
        onClick={handleSave}
      >
        儲存功能權限
      </Button>
    </div>
  );
}

// ─── 菜單可見性 Tab ──────────────────────────────────────────────────────────

interface MenuVisibilityTabProps {
  code: string;
  isAdmin: boolean;
}

/**
 * 菜單可見性 Tab（antd Tree checkable → PUT menu-hidden）。
 *
 * 顯示該角色 workbenchType 對應的 MENU_TREES；
 * 勾選 = 可見；缺少前置能力的節點 disabled + title 提示；
 * 未勾選的葉 key 收集為 menuKeys 調 PUT menu-hidden。
 */
function MenuVisibilityTab({ code, isAdmin }: MenuVisibilityTabProps) {
  const { data, isLoading } = useRoleDetail(code);
  const putMenuHidden = usePutMenuHidden(code);
  /** checkedKeys 狀態（null = 使用後端黑名單反算） */
  const [checkedKeys, setCheckedKeys] = useState<string[] | null>(null);

  const item = data?.item;

  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}>
        <Spin />
      </div>
    );
  }

  if (!item) return null;

  if (isAdmin) {
    return (
      <Alert
        type="warning"
        showIcon
        message="系統管理員角色鎖定不可編輯"
        style={{ maxWidth: 640 }}
      />
    );
  }

  /** 取工作台類型對應的基準樹（admin/manager 共用 admin 型樹） */
  const wbType =
    (item.workbenchType as keyof typeof MENU_TREES) in MENU_TREES
      ? (item.workbenchType as keyof typeof MENU_TREES)
      : 'admin';
  const baseTree = MENU_TREES[wbType];

  /** 全部葉 key */
  const allLeafKeys = collectAllLeafKeys(baseTree);

  /** 黑名單 Set（後端存的是隱藏 key） */
  const hiddenSet = new Set(item.menuHidden);

  /**
   * 當前勾選 key 清單：
   * - 有本地狀態（用戶已操作）→ 使用本地狀態
   * - 否則由「全部葉 key ∖ 黑名單」反算
   */
  const effectiveChecked = checkedKeys ?? allLeafKeys.filter((k) => !hiddenSet.has(k));

  /** 當前角色能力集 */
  const capSet = new Set<string>(item.capabilities);

  /** 建立 antd Tree DataNode */
  const treeData = buildTreeNodes(baseTree, capSet);

  /** Tree 勾選狀態改變 */
  const handleCheck: TreeProps['onCheck'] = useCallback((checked) => {
    // checked 可能是 string[] 或 { checked: string[]; halfChecked: string[] }
    const keys: string[] = Array.isArray(checked)
      ? (checked as string[])
      : ((checked as { checked: string[] }).checked ?? []);
    setCheckedKeys(keys);
  }, []);

  /** 儲存：以「未勾選的葉 key」作為 menuKeys 黑名單 */
  const handleSave = () => {
    const checkedSet = new Set(effectiveChecked);
    const hidden = allLeafKeys.filter((k) => !checkedSet.has(k));
    putMenuHidden.mutate(hidden);
  };

  return (
    <div style={{ maxWidth: 560 }}>
      <Alert
        type="info"
        showIcon
        message="說明"
        description="勾選 = 可見（用戶能在菜單中看到此項）；取消勾選 = 隱藏。需先在「功能權限」Tab 勾選對應能力，能力不足的菜單項（灰色）無法啟用。"
        style={{ marginBottom: 16 }}
      />

      <Tree
        checkable
        selectable={false}
        checkedKeys={effectiveChecked}
        onCheck={handleCheck}
        treeData={treeData}
        defaultExpandAll
      />

      <Divider />

      <Button
        type="primary"
        icon={<Save size={14} />}
        loading={putMenuHidden.isPending}
        onClick={handleSave}
        style={{ marginTop: 8 }}
      >
        儲存菜單可見性
      </Button>
    </div>
  );
}

// ─── 主組件 ──────────────────────────────────────────────────────────────────

/**
 * 角色詳情頁主組件。
 *
 * 從路由參數取得角色代碼，載入詳情後分發三個 Tab：
 * 基本信息 / 功能權限 / 菜單可見性。
 */
function RoleDetailPage() {
  const { code } = Route.useParams();
  const { tab } = Route.useSearch();
  const navigate = useNavigate();
  const { data, isLoading, isError } = useRoleDetail(code);

  const item = data?.item;
  /** admin 角色整頁只讀 */
  const isAdmin = code === 'admin';

  const activeTab = tab ?? 'info';

  /** Tab 切換時同步 URL searchParams */
  const handleTabChange = (key: string) => {
    void navigate({
      to: '/settings/roles/$code',
      params: { code },
      search: { tab: key as 'info' | 'capabilities' | 'menu' },
      replace: true,
    });
  };

  // ── 載入中 ──
  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 64 }}>
        <Spin size="large" />
      </div>
    );
  }

  // ── 錯誤 / 查無角色 ──
  if (isError || !item) {
    return (
      <div style={{ padding: 24 }}>
        <Alert
          type="error"
          showIcon
          message="無法載入角色詳情"
          description="該角色可能不存在，或您沒有存取權限。"
          action={
            <Button
              size="small"
              icon={<ArrowLeft size={13} />}
              onClick={() => void navigate({ to: '/settings/roles' })}
            >
              返回列表
            </Button>
          }
        />
      </div>
    );
  }

  return (
    <PageShell
      title={
        <span>
          角色詳情：<span style={{ fontWeight: weight.semibold }}>{item.name}</span>
        </span>
      }
      actions={
        <Button
          type="default"
          size="small"
          icon={<ArrowLeft size={14} />}
          onClick={() => void navigate({ to: '/settings/roles' })}
        >
          返回列表
        </Button>
      }
    >
      <Tabs
        activeKey={activeTab}
        onChange={handleTabChange}
        destroyInactiveTabPane={false}
        items={[
          {
            key: 'info',
            label: '基本信息',
            children: <BasicInfoTab code={code} isAdmin={isAdmin} />,
          },
          {
            key: 'capabilities',
            label: '功能權限',
            children: <CapabilitiesTab code={code} isAdmin={isAdmin} />,
          },
          {
            key: 'menu',
            label: '菜單可見性',
            children: <MenuVisibilityTab code={code} isAdmin={isAdmin} />,
          },
        ]}
      />
    </PageShell>
  );
}
